/**
 * Workbox runtime-caching rules for same-origin /api/v1 GETs (BUGS #22):
 * offline reads must survive a reload. Shared between vite.config.ts (the
 * generated service worker) and the config-shape tests that pin the rules.
 *
 * ORDER MATTERS: workbox matches routes in array order — the images rule sits
 * FIRST so attachment/photo streams hit CacheFirst and never enter (or evict)
 * the JSON cache.
 */

export const API_IMAGE_CACHE = 'api-images';
export const API_JSON_CACHE = 'api-json';

/**
 * Docuvault (2026-10-09): every /api/v1/vault/* answer — the folder and file
 * lists, and the file bytes themselves. They name contracts, change orders
 * and pay files, and Money and Restricted downloads are no-store on the
 * server for exactly this reason (design §3.5: "never cached in the
 * browser"); a copy here would be cookie-agnostic and outlive the session.
 * Neither rule below takes one, so the network answers or the read fails —
 * and pdf.js's range requests for a drawing set go straight through.
 */
export const DOCUVAULT_API_PATTERN = /\/api\/v1\/vault(?:[/?#]|$)/;

/**
 * Pay, employee personal records and medical data (Michael 2026-10-16, the
 * brief's offline policy: "do not store sensitive financial/employee content
 * offline by default"). Neither rule below takes these, so the network answers
 * or the read fails, and purgeSensitiveFromApiCaches() removes copies kept
 * before the rule existed.
 *  - pay: the posting queue and the payroll-wide time log;
 *  - employee personal records: the list, a record, its hours and certificate
 *    scans. The picker (/employees/lookup) and the names list stay: they
 *    carry no personal data and the crews pick people offline;
 *  - medical: an incident's medical page and case workspace, and the OSHA
 *    300/301 logs and privacy cases.
 */
const SENSITIVE_PATHS = [
  String.raw`employees(?!\/(?:lookup|names)(?:[/?#]|$))(?:[/?#]|$)`,
  String.raw`admin\/employees`,
  String.raw`timecards\/(?:posting|log)(?:[/?#]|$)`,
  String.raw`incidents\/[^/?#]+\/(?:medical|workspace)(?:[/?#]|$)`,
  String.raw`safety\/regulatory(?:[/?#]|$)`,
].join('|');

export const SENSITIVE_API_PATTERN = new RegExp(String.raw`\/api\/v1\/(?:${SENSITIVE_PATHS})`);

/** Binary streams: every nested .../attachments/{id} route (daily-log photos,
 *  incident photos). Immutable server-side → CacheFirst. Never a Docuvault
 *  address (DOCUVAULT_API_PATTERN) nor a sensitive one (cert scans). */
export const API_IMAGE_PATTERN = new RegExp(String.raw`\/api\/v1\/(?!vault(?:[/?#]|$)|${SENSITIVE_PATHS}).+\/attachments\/[^/?]+`);

/** Everything else under /api/v1 (JSON reads). NetworkFirst: fresh when the
 *  network answers within 3s, cached copy in a dead zone.
 *
 *  Except the two "today for yesterday" reads (2026-10-08): the day a new
 *  log opens on (daily-logs/suggested-date) and the Logs to write card
 *  (daily-logs/to-write). Each asks the same URL every day, so a copy from
 *  up to a day ago can name a day written since — a start would open a
 *  submitted log with nothing said, the card would list days already done.
 *  No route takes them, so the network answers or they fail: the start
 *  opens on today, the card hides.
 *
 *  Nor Docuvault, for its own reason — see DOCUVAULT_API_PATTERN — nor the
 *  sensitive reads (SENSITIVE_API_PATTERN). */
export const API_JSON_PATTERN = new RegExp(
  String.raw`\/api\/v1\/(?!daily-logs\/(?:suggested-date|to-write)(?:[/?#]|$)|vault(?:[/?#]|$)|${SENSITIVE_PATHS})`,
);

export const apiRuntimeCaching = [
  {
    urlPattern: API_IMAGE_PATTERN,
    method: 'GET' as const,
    handler: 'CacheFirst' as const,
    options: {
      cacheName: API_IMAGE_CACHE,
      expiration: {
        maxEntries: 60,                     // tight — blobs must not evict JSON
        maxAgeSeconds: 60 * 60 * 24 * 7,    // 7 days
      },
      // 401/403 (and everything non-OK) must never be cached.
      cacheableResponse: { statuses: [200] },
    },
  },
  {
    urlPattern: API_JSON_PATTERN,
    method: 'GET' as const,
    handler: 'NetworkFirst' as const,
    options: {
      cacheName: API_JSON_CACHE,
      networkTimeoutSeconds: 3,
      expiration: {
        maxEntries: 200,
        maxAgeSeconds: 60 * 60 * 24,        // 24 hours — field data staler than a day is a lie
      },
      cacheableResponse: { statuses: [200] },
    },
  },
];

/**
 * Identity safety (BUGS #21/#22): cached API responses are cookie-agnostic,
 * so they must die with the offline data — user B never reads user A's JSON.
 * Called from clearAllOfflineData (the single wipe-this-device choke point).
 */
/**
 * Copies of sensitive reads kept before SENSITIVE_API_PATTERN existed (or by
 * an older worker) are deleted from both API caches. The new worker runs this
 * when it activates; the rest of each cache is left alone.
 *
 * @returns how many copies were deleted
 */
export async function purgeSensitiveFromApiCaches(): Promise<number> {
  if (typeof caches === 'undefined') return 0;
  let removed = 0;
  try {
    for (const name of [API_JSON_CACHE, API_IMAGE_CACHE]) {
      if (!(await caches.has(name))) continue;
      const cache = await caches.open(name);
      for (const request of await cache.keys()) {
        if (SENSITIVE_API_PATTERN.test(request.url) && (await cache.delete(request))) removed += 1;
      }
    }
  } catch {
    // CacheStorage unavailable/denied — nothing readable to remove.
  }
  return removed;
}

export async function clearApiCaches(): Promise<void> {
  if (typeof caches === 'undefined') return; // non-SW contexts (tests, plain http)
  try {
    await Promise.all([caches.delete(API_JSON_CACHE), caches.delete(API_IMAGE_CACHE)]);
  } catch {
    // CacheStorage unavailable/denied — nothing cached to leak, nothing to do.
  }
}
